import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Alert,
    alpha,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Close as CloseIcon,
    InfoOutlined as InfoOutlinedIcon,
    VisibilityOutlined as VisibilityOutlinedIcon,
} from '@mui/icons-material';

import { getSavedMatching } from '../services/api';

function ReportMatchingTelemetryPage() {
    const { workspaceId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [matchResults, setMatchResults] = useState(location.state?.matchResults || null);
    const [telemetry, setTelemetry] = useState(location.state?.telemetry || location.state?.matchResults?.telemetry || null);
    const [telemetryDetail, setTelemetryDetail] = useState(location.state?.telemetryDetail || location.state?.matchResults?.telemetry_detail || null);
    const [selectedRow, setSelectedRow] = useState(null);

    const reportName = searchParams.get('report') || location.state?.legacyReportName || matchResults?.legacy_report || '';

    useEffect(() => {
        let active = true;
        const load = async () => {
            if (!workspaceId || !reportName) return;
            const loadedReportName = String(matchResults?.legacy_report || '').trim();
            const hasRequestedMatch = loadedReportName && loadedReportName === reportName;
            try {
                if (hasRequestedMatch && telemetryDetail) {
                    return;
                }
                if (!hasRequestedMatch || !telemetryDetail) {
                    setLoading(true);
                }
                setError('');
                const data = await getSavedMatching(workspaceId, reportName);
                if (!active) return;
                setMatchResults(data?.match_results || null);
                setTelemetry(data?.match_results?.telemetry || null);
                setTelemetryDetail(data?.match_results?.telemetry_detail || null);
            } catch (err) {
                if (!active) return;
                setError(err?.response?.data?.detail || 'Failed to load telemetry.');
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            active = false;
        };
    }, [workspaceId, reportName, matchResults, telemetryDetail]);

    const poolCards = useMemo(() => {
        const pools = telemetryDetail?.pools || {};
        return [
            {
                key: 'standard',
                title: 'Standard Pool',
                data: pools.standard || null,
                color: 'success',
            },
            {
                key: 'custom',
                title: 'Custom Pool',
                data: pools.custom || null,
                color: 'warning',
            },
            {
                key: 'standard_other_theme',
                title: 'Standard Other Theme Pool',
                data: pools.standard_other_theme || null,
                color: 'primary',
            },
        ];
    }, [telemetryDetail]);

    const formatPercent = (value) => {
        if (value === null || value === undefined || value === '') return '-';
        const numeric = Number(value);
        if (Number.isNaN(numeric)) return '-';
        return `${numeric.toFixed(1)}%`;
    };

    const formatScore = (value) => {
        if (value === null || value === undefined || value === '') return '-';
        const numeric = Number(value);
        if (Number.isNaN(numeric)) return '-';
        const percent = numeric <= 1 ? numeric * 100 : numeric;
        return `${percent.toFixed(1)}%`;
    };

    const normalizeBoolean = (value) => {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'string') {
            const normalized = value.trim().toLowerCase();
            if (normalized === 'true') return true;
            if (normalized === 'false') return false;
        }
        return null;
    };

    const normalizeFieldDetailRow = (row, { defaultAcceptedVerdict = false } = {}) => {
        if (!row || typeof row !== 'object') return null;

        let candidateTechnicalScore = null;
        const candidateList = Array.isArray(row.candidates) ? row.candidates : [];
        for (const candidate of candidateList) {
            if (!candidate || typeof candidate !== 'object') continue;
            candidateTechnicalScore = candidate.technical_score ?? candidate.vector_score ?? candidate.score ?? null;
            if (candidateTechnicalScore !== null && candidateTechnicalScore !== undefined) {
                break;
            }
        }

        const llmValidated = normalizeBoolean(row.llm_validated);
        let llmEquivalent = normalizeBoolean(row.llm_is_equivalent);
        if (llmEquivalent === null) {
            llmEquivalent = normalizeBoolean(row.llm_equivalent);
        }
        if (llmEquivalent === null && defaultAcceptedVerdict && llmValidated === true) {
            llmEquivalent = true;
        }

        const semanticScore = (
            row.semantic_score
            ?? row.technical_score
            ?? row.vector_score
            ?? row.schema_mapper_vector_score
            ?? candidateTechnicalScore
            ?? row.score
            ?? null
        );
        const llmScore = (
            row.llm_score
            ?? row.llm_confidence
            ?? row.schema_mapper_llm_confidence
            ?? null
        );

        return {
            legacy_key: row.legacy_key,
            legacy_column: row.legacy_column || row.legacy_logical || '',
            legacy_physical: row.legacy_physical || '',
            legacy_table: row.legacy_table || '',
            catalyst_column: row.catalyst_column || row.catalyst_logical || '',
            catalyst_physical: row.catalyst_physical || '',
            catalyst_table: row.catalyst_table || '',
            match_type: row.match_type || '',
            match_origin: row.match_origin || '',
            column_type: row.column_type || '',
            semantic_score: semanticScore,
            llm_score: llmScore,
            llm_reasoning: row.llm_reasoning || row.schema_mapper_llm_reasoning || '',
            llm_validated: llmValidated,
            llm_equivalent: llmEquivalent,
            preference_status: row.preference_status,
            validation_source: row.validation_source || '',
            validation_strategy: row.validation_strategy || '',
            counted_in_stage2: normalizeBoolean(row.counted_in_stage2),
            stage2_status: row.stage2_status || '',
            stage2_exclusion_reason: row.stage2_exclusion_reason || '',
        };
    };

    const stage2FieldDetailsByReport = useMemo(() => {
        const rowsByReport = new Map();
        const topMatches = Array.isArray(matchResults?.top_matches) ? matchResults.top_matches : [];
        topMatches.forEach((reportMatch) => {
            const catalystReportName = String(reportMatch?.catalyst_report_name || '').trim();
            if (!catalystReportName) return;
            const acceptedRows = Array.isArray(reportMatch?.matched_columns)
                ? reportMatch.matched_columns.map((row) => ({
                    ...row,
                    counted_in_stage2: row?.counted_in_stage2 ?? true,
                    stage2_status: row?.stage2_status || 'counted',
                }))
                : [];
            const invalidatedRows = Array.isArray(reportMatch?.invalidated_matches)
                ? reportMatch.invalidated_matches.map((row) => ({
                    ...row,
                    counted_in_stage2: false,
                    stage2_status: row?.stage2_status || 'rejected_by_llm',
                    stage2_exclusion_reason: row?.stage2_exclusion_reason || row?.llm_reasoning || '',
                }))
                : [];
            const rows = [...acceptedRows, ...invalidatedRows]
                .map((row) => normalizeFieldDetailRow(row, { defaultAcceptedVerdict: true }))
                .filter(Boolean);
            rowsByReport.set(catalystReportName, rows);
        });
        return rowsByReport;
    }, [matchResults]);

    const selectedStage1Rows = useMemo(() => (
        Array.isArray(selectedRow?.stage1_field_details)
            ? selectedRow.stage1_field_details.map((row) => normalizeFieldDetailRow(row)).filter(Boolean)
            : []
    ), [selectedRow]);

    const selectedStage2Rows = useMemo(() => {
        const reportRows = stage2FieldDetailsByReport.get(String(selectedRow?.catalyst_report_name || '').trim());
        if (reportRows) {
            return reportRows;
        }
        return Array.isArray(selectedRow?.stage2_field_details)
            ? selectedRow.stage2_field_details.map((row) => normalizeFieldDetailRow(row)).filter(Boolean)
            : [];
    }, [selectedRow, stage2FieldDetailsByReport]);
    const selectedStage2CountedCount = useMemo(() => (
        selectedStage2Rows.filter((row) => row?.counted_in_stage2 === true).length
    ), [selectedStage2Rows]);

    const getReasonLabel = (row) => {
        switch (row?.final_reason) {
        case 'shown_on_ui':
            return 'Shown on UI';
        case 'below_pool_display_threshold':
            return 'Below final threshold after Stage 2';
        case 'outside_pool_top_n_or_tie':
            return 'Passed Stage 2 but hidden by pool cutoff';
        case 'outside_pool_stage2_window':
            return 'Did not enter Stage 2 window';
        default:
            return row?.stage2_selected ? 'Entered Stage 2' : 'Stayed in Stage 1';
        }
    };

    const getMatchTypeMeta = (matchType) => {
        const value = String(matchType || '').trim().toLowerCase();
        if (!value) {
            return { label: '-', color: 'default' };
        }
        if (value.includes('exact')) {
            return { label: 'Exact', color: 'success' };
        }
        if (value.includes('semantic')) {
            return { label: 'Semantic', color: 'info' };
        }
        if (value.includes('recovery') || value.includes('llm')) {
            return { label: 'LLM Recovery', color: 'warning' };
        }
        return { label: matchType, color: 'default' };
    };

    const getSourceLabel = (row) => {
        const formatValidationSource = (value) => {
            const normalized = String(value || '').trim().toLowerCase();
            const labels = {
                fresh_llm: 'Fresh LLM validation',
                reuse_schema_mapper: 'Schema Mapper validation reused',
                schema_mapper: 'Schema Mapper validation reused',
                user_preference: 'User preference match',
                user_preference_rejected: 'Rejected user preference',
                same_theme_exact: 'Same-theme exact match',
                selected_target_report_exact: 'Exact match in selected report',
                top_contributing_tables_exact: 'Exact match in top contributing tables',
                none: 'Validation skipped',
            };
            return labels[normalized] || '';
        };
        const validationStrategy = formatValidationSource(row?.validation_strategy);
        const validationSource = formatValidationSource(row?.validation_source);
        const matchType = String(row?.match_type || '').trim();

        if (validationSource) {
            return validationSource;
        }
        if (validationStrategy) {
            return validationStrategy;
        }
        return matchType || '-';
    };

    const getLlmRunMeta = (row) => {
        if (row?.llm_validated === true) {
            return { label: 'Yes', color: 'success' };
        }
        if (row?.llm_validated === false) {
            return { label: 'No', color: 'default' };
        }
        return { label: '-', color: 'default' };
    };

    const getLlmVerdictMeta = (row) => {
        if (row?.llm_equivalent === true) {
            return { label: 'True', color: 'success' };
        }
        if (row?.llm_equivalent === false) {
            return { label: 'False', color: 'error' };
        }
        return { label: '-', color: 'default' };
    };

    const getCountedMeta = (row) => {
        if (row?.counted_in_stage2 === true) {
            return { label: 'Yes', color: 'success' };
        }
        if (row?.counted_in_stage2 === false) {
            return { label: 'No', color: 'error' };
        }
        return { label: '-', color: 'default' };
    };

    const renderFieldDetailsTable = (rows, emptyMessage, { showCountedColumn = false } = {}) => (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Legacy Field</TableCell>
                        <TableCell>Catalyst Field</TableCell>
                        <TableCell>Match Type</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell align="right">Semantic Score</TableCell>
                        <TableCell align="right">LLM Score</TableCell>
                        <TableCell align="center">LLM Run</TableCell>
                        <TableCell align="center">LLM Verdict</TableCell>
                        {showCountedColumn && <TableCell align="center">Counted</TableCell>}
                        <TableCell align="center">Reasoning</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={showCountedColumn ? 10 : 9} align="center" sx={{ py: 3 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {emptyMessage}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                    {rows.map((row, index) => {
                        const matchMeta = getMatchTypeMeta(row.match_type);
                        const legacyLabel = row.legacy_physical || row.legacy_column || '-';
                        const catalystLabel = row.catalyst_physical || row.catalyst_column || '-';
                        const reasoning = String(row.llm_reasoning || '').trim();
                        const llmScoreDisplay = row?.llm_score === null || row?.llm_score === undefined
                            ? '-'
                            : formatScore(row.llm_score);
                        const llmRunMeta = getLlmRunMeta(row);
                        const llmVerdictMeta = getLlmVerdictMeta(row);
                        const countedMeta = getCountedMeta(row);
                        const detailReasoning = String(row.stage2_exclusion_reason || reasoning).trim();
                        return (
                            <TableRow
                                key={`${row.legacy_key || index}-${row.catalyst_physical || row.catalyst_column || index}`}
                                sx={row.counted_in_stage2 === false ? { bgcolor: alpha(theme.palette.error.main, 0.06) } : undefined}
                            >
                                <TableCell sx={{ maxWidth: 260 }}>
                                    <Stack spacing={0.25}>
                                        <Typography variant="body2" fontWeight={600} noWrap title={legacyLabel}>
                                            {legacyLabel}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap title={row.legacy_column || ''}>
                                            {row.legacy_column || '-'}
                                        </Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell sx={{ maxWidth: 260 }}>
                                    <Stack spacing={0.25}>
                                        <Typography variant="body2" fontWeight={600} noWrap title={catalystLabel}>
                                            {catalystLabel}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap title={row.catalyst_column || ''}>
                                            {row.catalyst_column || '-'}
                                        </Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Chip size="small" color={matchMeta.color} variant="outlined" label={matchMeta.label} />
                                </TableCell>
                                <TableCell sx={{ maxWidth: 220 }}>
                                    <Typography variant="body2" noWrap title={getSourceLabel(row)}>
                                        {getSourceLabel(row)}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">{formatScore(row.semantic_score)}</TableCell>
                                <TableCell align="right">{llmScoreDisplay}</TableCell>
                                <TableCell align="center">
                                    <Chip size="small" color={llmRunMeta.color} variant="outlined" label={llmRunMeta.label} />
                                </TableCell>
                                <TableCell align="center">
                                    <Chip size="small" color={llmVerdictMeta.color} variant="outlined" label={llmVerdictMeta.label} />
                                </TableCell>
                                {showCountedColumn && (
                                    <TableCell align="center">
                                        <Chip size="small" color={countedMeta.color} variant="outlined" label={countedMeta.label} />
                                    </TableCell>
                                )}
                                <TableCell align="center">
                                    {detailReasoning ? (
                                        <Tooltip title={detailReasoning} arrow placement="left">
                                            <IconButton size="small" aria-label="View LLM reasoning">
                                                <InfoOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">-</Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const renderPoolTable = (poolKey, poolData) => {
        if (!poolData) return null;
        const rows = poolData.stage1_candidates || [];
        return (
            <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                    {poolData.pool === 'custom'
                        ? 'Custom'
                        : poolData.pool === 'standard_other_theme'
                            ? 'Standard Other Theme'
                            : 'Standard'} Stage 1 Candidates
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Rank</TableCell>
                                <TableCell>Report</TableCell>
                                <TableCell align="right">Stage 1 Score</TableCell>
                                <TableCell align="right">Stage 2 Score</TableCell>
                                <TableCell>Reason</TableCell>
                                <TableCell align="center">Details</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No candidates in this pool.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {rows.map((row) => (
                                <TableRow key={`${poolKey}-${row.stage1_rank}-${row.catalyst_report_name}`}>
                                    <TableCell>{row.stage1_rank}</TableCell>
                                    <TableCell sx={{ maxWidth: 420 }}>
                                        <Typography variant="body2" title={row.catalyst_report_name} noWrap>
                                            {row.catalyst_report_name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">{formatPercent(row.stage1_overlap_percentage ?? row.overlap_percentage)}</TableCell>
                                    <TableCell align="right">{formatPercent(row.stage2_overlap_percentage)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            color={row.displayed ? 'success' : row.stage2_selected ? 'secondary' : 'default'}
                                            variant={row.displayed || row.stage2_selected ? 'filled' : 'outlined'}
                                            label={getReasonLabel(row)}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="View field-level report details" arrow>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => setSelectedRow({
                                                    ...row,
                                                    poolKey,
                                                    poolLabel: poolData.pool === 'custom'
                                                        ? 'Custom'
                                                        : poolData.pool === 'standard_other_theme'
                                                            ? 'Standard Other Theme'
                                                            : 'Standard',
                                                })}
                                            >
                                                <VisibilityOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    return (
        <Box sx={{ p: 4 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                >
                    Back
                </Button>
                <Box>
                    <Typography variant="h5" fontWeight={700}>Match Telemetry</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {reportName || 'Report telemetry view'}
                    </Typography>
                </Box>
            </Stack>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && error && (
                <Alert severity="error">{error}</Alert>
            )}

            {!loading && !error && !telemetryDetail && (
                <Alert severity="info">No telemetry detail is available for this matching result.</Alert>
            )}

            {!loading && !error && telemetryDetail && (
                <Stack spacing={3}>
                    <Grid container spacing={3}>
                        {poolCards.map(({ key, title, data, color }) => (
                            <Grid item xs={12} md={6} key={key}>
                                <Card variant="outlined" sx={{ borderRadius: 3 }}>
                                    <CardContent>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                            <Typography variant="h6" fontWeight={700}>{title}</Typography>
                                            <Chip size="small" color={color} label={`${data?.display_count || 0} shown`} />
                                        </Stack>
                                        <Stack spacing={1}>
                                            <Typography variant="body2" color="text.secondary">
                                                Reports in scope: <strong>{data?.scope_report_count || 0}</strong>
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Stage 1 candidates: <strong>{data?.stage1_candidate_count || 0}</strong>
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Stage 2 window: <strong>{data?.stage2_candidate_count || 0}</strong> of requested top <strong>{data?.requested_top_n || 0}</strong> with multiplier <strong>{data?.stage2_multiplier || 0}</strong>
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Final UI count: <strong>{data?.display_count || 0}</strong>
                                            </Typography>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {poolCards.map(({ key, data }) => renderPoolTable(key, data))}

                    <Divider />

                    <Box>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                            Runtime Summary
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                {telemetry?.explanation || 'No textual telemetry explanation available.'}
                            </Typography>
                        </Paper>
                    </Box>

                    <Box>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                            Raw Telemetry
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, overflow: 'auto' }}>
                            <Box
                                component="pre"
                                sx={{
                                    m: 0,
                                    fontSize: 12,
                                    lineHeight: 1.5,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {JSON.stringify({ telemetryDetail, telemetry, matchResultsSummary: telemetryDetail?.combined || {} }, null, 2)}
                            </Box>
                        </Paper>
                    </Box>
                </Stack>
            )}

            <Dialog
                open={Boolean(selectedRow)}
                onClose={() => setSelectedRow(null)}
                fullWidth
                maxWidth="xl"
            >
                <DialogTitle sx={{ pr: 6 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                        <Box>
                            <Typography variant="h6" fontWeight={700}>
                                {selectedRow?.catalyst_report_name || 'Report Details'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedRow?.poolLabel || 'Pool'} candidate
                            </Typography>
                        </Box>
                        <IconButton onClick={() => setSelectedRow(null)} sx={{ mt: -0.5, mr: -1 }}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedRow && (
                        <Stack spacing={3}>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip size="small" color="primary" variant="outlined" label={`Stage 1 ${formatPercent(selectedRow.stage1_overlap_percentage ?? selectedRow.overlap_percentage)}`} />
                                <Chip size="small" color={selectedRow.stage2_selected ? 'secondary' : 'default'} variant="outlined" label={`Stage 2 ${formatPercent(selectedRow.stage2_overlap_percentage)}`} />
                                <Chip size="small" color={selectedRow.displayed ? 'success' : 'default'} variant={selectedRow.displayed ? 'filled' : 'outlined'} label={getReasonLabel(selectedRow)} />
                                <Chip size="small" variant="outlined" label={`Stage 1 Fields ${selectedRow.stage1_field_count || 0}`} />
                                <Chip size="small" variant="outlined" label={`Stage 2 Fields ${selectedRow.stage2_field_count || 0}`} />
                                <Chip size="small" variant="outlined" label={`Stage 2 Counted ${selectedRow.stage2_counted_field_count ?? selectedStage2CountedCount}`} />
                            </Stack>

                            <Box>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                                    Stage 1 Field Matches
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                    Saved Stage 1 rows are pre-verification diagnostics. LLM columns are only populated when that data already exists in the saved result.
                                </Typography>
                                {renderFieldDetailsTable(
                                    selectedStage1Rows,
                                    'No Stage 1 field details are available for this report.'
                                )}
                            </Box>

                            <Box>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                                Stage 2 Verified Field Matches
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                    Stage 2 shows every reviewed field from the saved match result. Rows marked Counted = No were reviewed in Stage 2 but excluded from the final Stage 2 score. Opening telemetry does not rerun matching or LLM validation.
                            </Typography>
                                {renderFieldDetailsTable(
                                    selectedStage2Rows,
                                    selectedRow.stage2_selected
                                        ? 'No Stage 2 verified field details are available for this report.'
                                        : 'This report did not enter the Stage 2 verification window.',
                                    { showCountedColumn: true }
                                )}
                            </Box>
                        </Stack>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default ReportMatchingTelemetryPage;
